/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useMemo } from "react";
import { bool, func, shape } from "prop-types";
import { connect } from "react-redux";

import { Agenda, TransferExecuteToContact, transfersTestIds } from "@ui-kit/components/index.es";

import { selectors as formSelectors } from "reducers/form";
import { selectors as frequentDestinationSelectors } from "reducers/frequentDestination";
import { selectors as i18nSelectors } from "reducers/i18n";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";
import { selectors as templateSelectors } from "reducers/template";
import { selectors as transferSelectors } from "reducers/transfer";

import Message from "pages/v2/transfer/scheduler/Message";
import useTransferToContactExecuteLogic from "v3/pages/transfers/logic/useTransferToContactExecuteLogic";
import { createTransferExecuteFormLabels } from "v3/pages/transfers/transferExecuteLabels";
import {
    TransferExecuteModals,
    useTransferExecuteFormikSync,
    useTransferExecuteLimitsRenderer,
    resolveTransferExecuteCurrency,
} from "v3/pages/transfers/transferExecuteBridgeShared";

function TransferExecuteToContactBridge(props) {
    const logic = useTransferToContactExecuteLogic(props);
    const getLabels = useMemo(() => createTransferExecuteFormLabels("toContact"), []);
    const renderLimits = useTransferExecuteLimitsRenderer(logic);

    const renderScheduleSummary = useCallback(() => {
        if (logic.values?.scheduler?.selectedOption === undefined) {
            return null;
        }
        return <Message value={logic.values.scheduler || { valueDate: "" }} />;
    }, [logic.values?.scheduler]);

    const formikFields = useMemo(
        () => ({
            debitAccount: logic.debitAccount,
            transferKind: logic.transferKind,
            concept: logic.concept,
            creditAccount: logic.creditAccount,
            amount: logic.amount,
            reference: logic.reference,
        }),
        [logic.debitAccount, logic.transferKind, logic.concept, logic.creditAccount, logic.amount, logic.reference],
    );

    useTransferExecuteFormikSync(logic.formikProps, formikFields);

    const renderAgenda = useCallback(
        () => (
            <Agenda
                data-testid={transfersTestIds.execute.agenda}
                styleV3
                contactsArray={logic.agendaItems}
                activeContact={logic.creditAccount}
                id="AgendaElement"
                idContainer="app"
                showAddContact={false}
                showSearcher
                showReference={logic.enableNewScheduleCard}
                showSwitchSameTitular={logic.enableNewScheduleCard}
                initialValueFilterbySameTitular={logic.agendaInitialSameHolderFilter}
                oneContactCallback={logic.handleAgendaOneContactSelect}
                onChangeSameHolderFilter={logic.handleSameHolderFilterChange}
            />
        ),
        [logic],
    );

    const showForm = logic.creditAccountConfirmed;

    const handleTadInfoClick = useCallback(() => {
        logic.openTADModal();
    }, [logic]);

    return (
        <>
            <TransferExecuteToContact
                getLabels={getLabels}
                loading={logic.showInitialSpinner}
                loadingSubmit={logic.fetchingSubmit}
                loadingDraft={logic.fetchingDraft}
                initialValues={logic.initialValues}
                validationSchema={logic.validationSchema}
                onSubmit={logic.onSubmit}
                setFormikProps={logic.setFormikProps}
                contactSectionTitle={getLabels("contactSectionTitle")}
                renderAgenda={renderAgenda}
                showForm={showForm}
                creditAccountConfirmed={logic.creditAccountConfirmed}
                creditAccountSearchCallback={logic.handleAccountSearch}
                debitAccountOptions={logic.mapAccounts}
                transferKindOptions={logic.transferKindOptions}
                conceptOptions={logic.conceptsFiltered}
                onDebitAccountChange={logic.handleDebitAccountSelect}
                hideDebitAmount={logic.enableHideAmounts && logic.hideAmount && logic.isMobile}
                currency={resolveTransferExecuteCurrency(logic.selectedCurrency)}
                referenceMaxLength={logic.referenceMaxLength}
                extendedAmountEnabled={logic.extendedAmountVariableEnabled}
                showTadWarning={logic.tadWarningShowed}
                tadHelperText={logic.tadWarningText}
                renderLimits={showForm ? renderLimits : undefined}
                showScheduleButton={logic.schedulerButtonVisible}
                onScheduleClick={logic.openScheduleModal}
                renderScheduleSummary={renderScheduleSummary}
                onSaveDraft={(values) => logic.handleSaveDraftClick({ values })}
                onSaveTemplate={(values) => logic.handleCreateTemplate({ values })}
                isDraftInactive={logic.isDraftButtonInactive}
                isTemplateInactive={false}
                continueExtraInactive={logic.isContinueInactive}
                sendEmail={logic.sendEmail}
                showFullList={logic.showFullList}
                onSendEmailToggle={logic.handleSendEmailToggle}
                onReferenceChange={logic.handleReferenceChange}
                onEmailChange={logic.handleEmailChange}
                onEmailTextChange={logic.handleEmailTextChange}
                onTransferKindChange={logic.handleTransferKindSelect}
                onConceptChange={logic.handleConceptSelect}
                onAmountBlur={logic.handleAmountBlur}
                onAmountKeyDown={logic.customHandleKeyDown}
                onTadInfoClick={handleTadInfoClick}
            />
            <TransferExecuteModals logic={logic} idActivity={logic.idActivity} idForm={logic.idForm} />
        </>
    );
}

TransferExecuteToContactBridge.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    onCreateTemplate: func.isRequired,
    schedulerButtonVisible: bool.isRequired,
    setSchedulerButtonVisible: func.isRequired,
    isTemplatesVisible: bool.isRequired,
    setIsTemplatesVisible: func.isRequired,
    location: shape({}).isRequired,
    isValidCbu: func.isRequired,
    summary: shape({}).isRequired,
    creditAccountDetail: shape({}),
};

TransferExecuteToContactBridge.defaultProps = {
    creditAccountDetail: null,
};

const mapStateToProps = (state) => ({
    fetching: transferSelectors.getFetching(state),
    fetchingDraft: formSelectors.getFetchingDraft(state),
    fetchingSubmit: transferSelectors.getFetchingSubmit(state),
    fetchingFrequentDestinations: frequentDestinationSelectors.getFetching(state),
    accounts: transferSelectors.getAccounts(state),
    concepts: transferSelectors.getConcepts(state),
    limit: transferSelectors.getLimit(state),
    limitUsed: transferSelectors.getLimitUsed(state),
    limits: transferSelectors.getLimits(state),
    summary: transferSelectors.getSummary(state),
    templates: templateSelectors.getTemplateList(state),
    isCVU: transferSelectors.getIsCVU(state),
    creditAccountDetail: transferSelectors.getCreditAccountDetails(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    lang: i18nSelectors.getLang(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    hideAmount: settingsSelectors.getUserData(state)?.hideBalance,
    idEnvironment: frequentDestinationSelectors.getEnviromentId(state),
    listFrequentDestinations: frequentDestinationSelectors.getListFrequentDestinations(state),
    selectedFromAgenda: transferSelectors.getSelectedFromAgenda(state),
    isSameHolderTransfer: frequentDestinationSelectors.getSameHolderTransfer(state),
    isSameHolderTransferForm: frequentDestinationSelectors.getSameHolderTransferForm(state),
});

export default connect(mapStateToProps)(TransferExecuteToContactBridge);
